import React from 'react';
import { useTranslation } from 'react-i18next';
import { Tool } from '../data/tools';
import { ToolPageTemplate } from './ToolPageTemplate';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/Card';

// Import tool components
import { JsonFormatterTool } from './tools/JsonFormatterTool';
import { Base64Tool } from './tools/Base64Tool';
import { ColorConverterTool } from './tools/ColorConverterTool';
import { PasswordGeneratorTool } from './tools/PasswordGeneratorTool';
import { HashGeneratorTool } from './tools/HashGeneratorTool';
import { TextDiffTool } from './tools/TextDiffTool';
import { WhoisLookupTool } from './tools/WhoisLookupTool';
import { DnsLookupTool } from './tools/DnsLookupTool';
import { PingTestTool } from './tools/PingTestTool';
import { PortScannerTool } from './tools/PortScannerTool';
import { RandomGeneratorTool } from './tools/RandomGeneratorTool';
import { CalculatorTool } from './tools/CalculatorTool';
import { PercentageCalculatorTool } from './tools/PercentageCalculatorTool';
import { JwtDecoderTool } from './tools/JwtDecoderTool';
import { CsvToJsonTool } from './tools/CsvToJsonTool';
import { JsonToCsvTool } from './tools/JsonToCsvTool';
import { PasswordStrengthCheckerTool } from './tools/PasswordStrengthCheckerTool';
import { DateCalculatorTool } from './tools/DateCalculatorTool';
import { TimezoneConverterTool } from './tools/TimezoneConverterTool';
import { AgeCalculatorTool } from './tools/AgeCalculatorTool';
import { XmlFormatterTool } from './tools/XmlFormatterTool';
import { SqlFormatterTool } from './tools/SqlFormatterTool';
import { CountdownTimerTool } from './tools/CountdownTimerTool';
import { WorkingDaysCalculatorTool } from './tools/WorkingDaysCalculatorTool';
import { SslCertificateAnalyzerTool } from './tools/SslCertificateAnalyzerTool';
import { ImageWatermarkTool } from './tools/ImageWatermarkTool';
import { ImageCompressTool } from './tools/ImageCompressTool';
import { ImageFormatConverterTool } from './tools/ImageFormatConverterTool';
import { ImageCropTool } from './tools/ImageCropTool';
import { ImageResizeTool } from './tools/ImageResizeTool';
import { ImageColorPickerTool } from './tools/ImageColorPickerTool';
import { UuidGeneratorTool } from './tools/UuidGeneratorTool';
import { UrlEncoderTool } from './tools/UrlEncoderTool';
import { QrGeneratorTool } from './tools/QrGeneratorTool';
import { TimestampConverterTool } from './tools/TimestampConverterTool';
import { RegexTesterTool } from './tools/RegexTesterTool';
import { IpLookupTool } from './tools/IpLookupTool';
import { MarkdownEditorTool } from './tools/MarkdownEditorTool';
import { UnitConverterTool } from './tools/UnitConverterTool';
import { BaseConverterTool } from './tools/BaseConverterTool';
import { HtmlEncoderTool } from './tools/HtmlEncoderTool';
import { CssMinifierTool } from './tools/CssMinifierTool';
import { JsMinifierTool } from './tools/JsMinifierTool';
import { TextCaseConverterTool } from './tools/TextCaseConverterTool';
import { WordCounterTool } from './tools/WordCounterTool';
import { LoremGeneratorTool } from './tools/LoremGeneratorTool';
import { TextToSpeechTool } from './tools/TextToSpeechTool';

interface ToolDisplayProps {
  tool: Tool | null;
  className?: string;
}

// Tool component mapping
const toolComponents: Record<string, React.ComponentType> = {
  // Developer Tools
  'json-formatter': JsonFormatterTool,
  'base64-encoder': Base64Tool,
  'url-encoder': UrlEncoderTool,
  'uuid-generator': UuidGeneratorTool,
  'hash-generator': HashGeneratorTool,
  'regex-tester': RegexTesterTool,
  'html-encoder': HtmlEncoderTool,
  'css-minifier': CssMinifierTool,
  'js-minifier': JsMinifierTool,

  // Converter Tools
  'color-converter': ColorConverterTool,
  'timestamp-converter': TimestampConverterTool,
  'qr-generator': QrGeneratorTool,
  'unit-converter': UnitConverterTool,
  'base-converter': BaseConverterTool,

  // Text Tools
  'markdown-editor': MarkdownEditorTool,
  'text-diff': TextDiffTool,
  'password-generator': PasswordGeneratorTool,
  'text-case-converter': TextCaseConverterTool,
  'word-counter': WordCounterTool,
  'lorem-generator': LoremGeneratorTool,
  'text-to-speech': TextToSpeechTool,

  // Image Tools
  'image-watermark': ImageWatermarkTool,
  'image-compress': ImageCompressTool,
  'image-format-converter': ImageFormatConverterTool,
  'image-crop': ImageCropTool,
  'image-resize': ImageResizeTool,
  'image-color-picker': ImageColorPickerTool,

  // Network Tools
  'whois-lookup': WhoisLookupTool,
  'ip-lookup': IpLookupTool,
  'dns-lookup': DnsLookupTool,
  'ping-test': PingTestTool,
  'port-scanner': PortScannerTool,

  // Math Tools
  'calculator': CalculatorTool,
  'percentage-calculator': PercentageCalculatorTool,
  'random-generator': RandomGeneratorTool,

  // DateTime Tools
  'date-calculator': DateCalculatorTool,
  'timezone-converter': TimezoneConverterTool,
  'countdown-timer': CountdownTimerTool,
  'age-calculator': AgeCalculatorTool,
  'working-days-calculator': WorkingDaysCalculatorTool,

  // Data Tools
  'csv-to-json': CsvToJsonTool,
  'json-to-csv': JsonToCsvTool,
  'xml-formatter': XmlFormatterTool,
  'sql-formatter': SqlFormatterTool,

  // Security Tools
  'password-strength-checker': PasswordStrengthCheckerTool,
  'jwt-decoder': JwtDecoderTool,
  'ssl-certificate-analyzer': SslCertificateAnalyzerTool,
};

export const ToolDisplay: React.FC<ToolDisplayProps> = ({ tool, className = '' }) => {
  const { t } = useTranslation();

  if (!tool) {
    return null; // HomePage will handle the default display
  }

  const ToolComponent = toolComponents[tool.id];

  return (
    <ToolPageTemplate tool={tool} className={className}>
      {ToolComponent ? (
        <ToolComponent />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>{t('common.comingSoon')}</CardTitle>
          </CardHeader>
          <CardContent>
            <CardDescription>
              {t('common.toolNotImplemented')}
            </CardDescription>
          </CardContent>
        </Card>
      )}
    </ToolPageTemplate>
  );
};


